import { usePrompt } from '@/components/ui/PromptDialog';
import { Button } from '@/components/ui/Button';
import { posApi, type PosHardwareConfig } from '@/api/pos.api';
import { usePosHardware } from '@/pages/pos/PosHardwareContext';
import { notify } from '@/core/toast/notify';

const card = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900';

interface DeviceCardProps {
  title: string;
  enabled: boolean;
  connected: boolean;
  connectionLabel?: string;
  onEnable: (enabled: boolean) => void;
  children?: React.ReactNode;
}

function DeviceCard({ title, enabled, connected, connectionLabel, onEnable, children }: DeviceCardProps) {
  return (
    <div className={card}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-semibold text-slate-800 dark:text-white">{title}</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => onEnable(e.target.checked)} />
          Enabled
        </label>
      </div>
      {enabled && (
        <>
          <p className={`mb-3 text-xs ${connected ? 'text-emerald-600' : 'text-amber-600'}`}>
            {connected ? `● Connected${connectionLabel ? `: ${connectionLabel}` : ''}` : '○ Not connected'}
          </p>
          {children}
        </>
      )}
    </div>
  );
}

interface PosHardwareSetupPanelProps {
  storeId: number;
  hardware: PosHardwareConfig;
  onSaved: () => void;
}

export function PosHardwareSetupPanel({ storeId, hardware, onSaved }: PosHardwareSetupPanelProps) {
  const { prompt, dialog: promptDialog } = usePrompt();
  const hw = usePosHardware();

  const saveFlag = async (patch: Partial<PosHardwareConfig>) => {
    try {
      await posApi.saveHardwareConfig({ ...hardware, storeId, ...patch });
      await hw.reloadConfig();
      onSaved();
      notify.success('Hardware config saved');
    } catch {
      notify.error('Failed to save hardware config');
    }
  };

  const testLabelPrint = async () => {
    const productId = await prompt({
      title: 'Test label print',
      label: 'Product ID',
    });
    if (!productId) return;
    try {
      await hw.printBarcodeLabel(Number(productId), 1);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Test print failed');
    }
  };

  return (
    <>
    {promptDialog}
    <div className="grid gap-4 lg:grid-cols-2">
      <DeviceCard
        title="Weighing scale"
        enabled={!!hardware.scaleEnabled}
        connected={!!hw.prefs.scaleConnected}
        connectionLabel={hw.prefs.scalePortLabel}
        onEnable={(v) => void saveFlag({ scaleEnabled: v })}
      >
        <p className="mb-2 text-xs text-slate-500">
          USB/RS-232 scale via Web Serial (Chrome/Edge). Weight auto-fills on the billing screen.
        </p>
        <div className="flex flex-wrap gap-2">
          {hw.serialSupported ? (
            <Button type="button" size="sm" onClick={() => void hw.connectScale()}>
              Connect scale
            </Button>
          ) : (
            <span className="text-xs text-slate-500">Web Serial not supported — use manual weight entry</span>
          )}
          {hw.prefs.scaleConnected && (
            <Button type="button" variant="secondary" size="sm" onClick={() => void hw.disconnectScale()}>
              Disconnect
            </Button>
          )}
        </div>
      </DeviceCard>

      <DeviceCard
        title="Barcode / label printer"
        enabled={!!hardware.barcodePrinterEnabled}
        connected={hw.barcodePrinterReady}
        connectionLabel={hw.prefs.barcodePrinterPortLabel}
        onEnable={(v) => void saveFlag({ barcodePrinterEnabled: v })}
      >
        <p className="mb-2 text-xs text-slate-500">
          Print product labels from billing (F11 on highlighted product). Uses 50×30 mm label layout.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={hw.connectBarcodePrinterBrowser}>
            Connect (browser print)
          </Button>
          {hw.serialSupported && (
            <Button type="button" variant="secondary" size="sm" onClick={() => void hw.connectBarcodePrinterSerial()}>
              Connect (USB serial)
            </Button>
          )}
          {hw.prefs.barcodePrinterConnected && (
            <Button type="button" variant="secondary" size="sm" onClick={() => void hw.disconnectBarcodePrinter()}>
              Disconnect
            </Button>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => void testLabelPrint()}>
            Test print
          </Button>
        </div>
      </DeviceCard>

      <DeviceCard
        title="Thermal receipt printer"
        enabled={hardware.thermalPrinterEnabled !== false}
        connected={!!hw.prefs.receiptPrinterConnected}
        connectionLabel={hw.prefs.receiptPrinterPortLabel}
        onEnable={(v) => void saveFlag({ thermalPrinterEnabled: v })}
      >
        <p className="mb-2 text-xs text-slate-500">Receipts print automatically after each sale via browser print dialog.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={hw.connectReceiptPrinterBrowser}>
            Connect (browser print)
          </Button>
          {hw.serialSupported && (
            <Button type="button" variant="secondary" size="sm" onClick={() => void hw.connectReceiptPrinterSerial()}>
              Connect (USB serial)
            </Button>
          )}
          {hw.prefs.receiptPrinterConnected && (
            <Button type="button" variant="secondary" size="sm" onClick={() => void hw.disconnectReceiptPrinter()}>
              Disconnect
            </Button>
          )}
        </div>
      </DeviceCard>

      <DeviceCard
        title="Cash drawer"
        enabled={hardware.cashDrawerEnabled !== false}
        connected={!!hw.prefs.receiptPrinterConnected && hw.prefs.receiptPrinterMode === 'serial'}
        connectionLabel={
          hw.prefs.receiptPrinterMode === 'serial' ? 'Via receipt printer serial' : 'Requires serial receipt printer'
        }
        onEnable={(v) => void saveFlag({ cashDrawerEnabled: v })}
      >
        <p className="mb-2 text-xs text-slate-500">
          Opens on cash sales when connected to a serial ESC/POS receipt printer.
        </p>
        <Button type="button" variant="secondary" size="sm" onClick={() => void hw.testCashDrawer()}>
          Test drawer kick
        </Button>
      </DeviceCard>

      <DeviceCard
        title="Customer display"
        enabled={!!hardware.customerDisplayEnabled}
        connected={!!hardware.customerDisplayEnabled}
        connectionLabel="Second screen / window"
        onEnable={(v) => void saveFlag({ customerDisplayEnabled: v })}
      >
        <p className="mb-2 text-xs text-slate-500">
          Opens automatically when billing starts. Shows cart total to the customer on a second monitor or window.
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            const token = sessionStorage.getItem('fattahone_display_token');
            if (token) {
              window.open(`/pos/display/${token}`, 'pos-customer-display', 'width=480,height=720');
            } else {
              notify.error('Open billing terminal first to create a display session');
            }
          }}
        >
          Open customer display
        </Button>
      </DeviceCard>

      <DeviceCard
        title="Barcode scanner"
        enabled
        connected
        connectionLabel="USB HID keyboard mode"
        onEnable={() => {}}
      >
        <p className="text-xs text-slate-500">
          Plug-and-play — no pairing needed. Scanners act as a keyboard and feed the product search field on the billing
          screen (F1).
        </p>
      </DeviceCard>
    </div>
    </>
  );
}
